import { client } from '../apollo/client'
import { HOURLY_PAIR_STATS_PAGE, HOURLY_TOKEN_STATS_PAGE } from '../apollo/queries'
import dayjs from 'dayjs'
import utc from 'dayjs/plugin/utc'

dayjs.extend(utc)

/**
 * Fetch hourlyPairStatistics for the last 48h via pagination.
 * Splits into two rolling 24h windows to compute volume AND % change,
 * matching production's block-based delta approach.
 *
 * Also derives per-token volumes from pair data (each token credited with the
 * full pair swap volume, matching production's methodology).
 *
 * Returns:
 *   pairVolumeMap        — { pairId: volume } for current 24h window (now-24h → now)
 *   prevPairVolumeMap    — { pairId: volume } for previous 24h window (now-48h → now-24h)
 *   totalVolume          — global sum for current 24h
 *   prevTotalVolume      — global sum for previous 24h
 *   tokenVolumeMap       — { tokenId: volume } current 24h derived from pairs
 *   prevTokenVolumeMap   — { tokenId: volume } previous 24h derived from pairs
 */
export async function fetchHourlyPairVolumes() {
  const nowSec = dayjs.utc().unix()
  const cutoff24h = nowSec - 86400
  const cutoff48h = nowSec - 172800

  const pairVolumeMap = {}
  const prevPairVolumeMap = {}
  const tokenVolumeMap = {}
  const prevTokenVolumeMap = {}
  let skip = 0

  while (true) {
    const result = await client
      .query({
        query: HOURLY_PAIR_STATS_PAGE,
        variables: { skip },
        fetchPolicy: 'cache-first',
      })
      .catch(() => ({ data: { hourlyPairStatistics: [] } }))

    const records = result?.data?.hourlyPairStatistics ?? []
    if (records.length === 0) break

    let foundOldRecord = false
    for (const r of records) {
      const ts = parseInt(r.hourStartUnix)
      const vol = parseFloat(r.hourlyVolumeUSD || 0)
      const pid = r.pair.id
      const t0 = r.token0?.id
      const t1 = r.token1?.id

      if (ts >= cutoff24h) {
        pairVolumeMap[pid] = (pairVolumeMap[pid] ?? 0) + vol
        if (t0) tokenVolumeMap[t0] = (tokenVolumeMap[t0] ?? 0) + vol
        if (t1) tokenVolumeMap[t1] = (tokenVolumeMap[t1] ?? 0) + vol
      } else if (ts >= cutoff48h) {
        prevPairVolumeMap[pid] = (prevPairVolumeMap[pid] ?? 0) + vol
        if (t0) prevTokenVolumeMap[t0] = (prevTokenVolumeMap[t0] ?? 0) + vol
        if (t1) prevTokenVolumeMap[t1] = (prevTokenVolumeMap[t1] ?? 0) + vol
      } else {
        foundOldRecord = true
      }
    }

    if (foundOldRecord || records.length < 1000) break
    skip += 1000
  }

  const totalVolume = Object.values(pairVolumeMap).reduce((sum, v) => sum + v, 0)
  const prevTotalVolume = Object.values(prevPairVolumeMap).reduce((sum, v) => sum + v, 0)
  return { pairVolumeMap, prevPairVolumeMap, totalVolume, prevTotalVolume, tokenVolumeMap, prevTokenVolumeMap }
}

/**
 * Fetch hourlyTokenStatistics for the last 48h via pagination.
 * Splits into two rolling 24h windows for volume + % change.
 * Falls back gracefully if the indexer doesn't support hourlyTokenStatistics.
 *
 * Returns:
 *   tokenVolumeMap     — { tokenId: volume } current 24h
 *   prevTokenVolumeMap — { tokenId: volume } previous 24h (for % change)
 *   hasData            — false if indexer returned nothing (use daily stats fallback)
 */
export async function fetchHourlyTokenVolumes() {
  const nowSec = dayjs.utc().unix()
  const cutoff24h = nowSec - 86400
  const cutoff48h = nowSec - 172800

  const tokenVolumeMap = {}
  const prevTokenVolumeMap = {}
  let skip = 0
  let hasData = false

  while (true) {
    const result = await client
      .query({
        query: HOURLY_TOKEN_STATS_PAGE,
        variables: { skip },
        fetchPolicy: 'cache-first',
      })
      .catch(() => ({ data: { hourlyTokenStatistics: [] } }))

    const records = result?.data?.hourlyTokenStatistics ?? []
    if (records.length === 0) break

    hasData = true
    let foundOldRecord = false
    for (const r of records) {
      const ts = parseInt(r.hourStartUnix)
      const vol = parseFloat(r.hourlyVolumeUSD || 0)
      const tid = r.token.id

      if (ts >= cutoff24h) {
        tokenVolumeMap[tid] = (tokenVolumeMap[tid] ?? 0) + vol
      } else if (ts >= cutoff48h) {
        prevTokenVolumeMap[tid] = (prevTokenVolumeMap[tid] ?? 0) + vol
      } else {
        foundOldRecord = true
      }
    }

    if (foundOldRecord || records.length < 1000) break
    skip += 1000
  }

  return { tokenVolumeMap, prevTokenVolumeMap, hasData }
}
